// src/component/admin/memior/CustomerMemoirDetailModal.jsx
import React, { useState, useMemo } from 'react';
import {
  CloseIcon,
  PhoneIcon,
  EmailIcon,
  ClockIcon,
  CheckCircleIcon,
  SendICon,
  ChatIcon,
  ArrowDown,
  CautionIcon,
  EditPencil,
  DeleteICon
} from '../../../public/icons';

/* ═══════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════ */

const formatDate = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return '—';
  }
};

const formatShortDate = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('en-GB'); // dd/mm/yyyy
  } catch {
    return '—';
  }
};

const STATUS_MAP = {
  pending: {
    label: 'Pending',
    badge: 'bg-[#FFF4D6] text-[#D8A100]',
    dot: 'bg-[#D8A100]'
  },
  processing: {
    label: 'Processing',
    badge: 'bg-[#E7ECF4] text-[#2F578F]',
    dot: 'bg-[#2F578F]'
  },
  active: {
    label: 'Active',
    badge: 'bg-[#E7F4ED] text-[#2F8F5B]',
    dot: 'bg-[#2F8F5B]'
  },
  completed: {
    label: 'Completed',
    badge: 'bg-[#E7F4ED] text-[#2F8F5B]',
    dot: 'bg-[#2F8F5B]'
  },
  cancelled: {
    label: 'Cancelled',
    badge: 'bg-[#F4E7E7] text-[#A82121]',
    dot: 'bg-[#A82121]'
  }
};

const PAYMENT_MAP = {
  pending: { label: 'Pending', badge: 'bg-[#FFF4D6] text-[#D8A100]' },
  processing: { label: 'Processing', badge: 'bg-[#E7ECF4] text-[#2F578F]' },
  paid: { label: 'Paid', badge: 'bg-[#E7F4ED] text-[#2F8F5B]' },
  failed: { label: 'Failed', badge: 'bg-[#F4E7E7] text-[#A82121]' },
  refunded: { label: 'Refunded', badge: 'bg-[#F2F4F7] text-[#6B635D]' }
};

const FREQUENCY_LABEL = {
  weekly: 'Weekly',
  daily: 'Daily',
  biweekly: 'Bi-weekly'
};

const THEME_LABEL = {
  wine: 'Wine',
  midnightBlue: 'Midnight Blue'
};

/* ═══════════════════════════════════════════════════════════
   Modal
   ═══════════════════════════════════════════════════════════ */

const CustomerMemoirDetailModal = ({ isOpen, onClose, memoir, onSendQuestions }) => {
  const [activeStoryTab, setActiveStoryTab] = useState('timelines');
  const [sendingWeek, setSendingWeek] = useState(null);

  /* ── Normalize the memoir prop into the shape the UI expects ── */
  const memoirData = useMemo(() => {
    if (!memoir) return null;

    const delivery = memoir.deliveryDetails || {};

    const deliveryName =
      `${delivery.firstName || ''} ${delivery.lastName || ''}`.trim();
    const userName = memoir.user
      ? `${memoir.user.firstName || ''} ${memoir.user.lastName || ''}`.trim()
      : '';
    const customerName = deliveryName || userName || 'Guest';

    const totalQuestions =
      memoir.totalQuestions ||
      (memoir.totalWeeks && memoir.questionFrequency
        ? Math.round(
            memoir.totalWeeks *
              (memoir.questionFrequency === 'daily'
                ? 7
                : memoir.questionFrequency === 'biweekly'
                ? 0.5
                : 1)
          )
        : (memoir.totalWeeks || 12) * 1);

    const completionPct =
      memoir.totalWeeks > 0
        ? Math.min(
            100,
            Math.round(((memoir.currentWeek || 0) / memoir.totalWeeks) * 100)
          )
        : 0;

    const pendingResponses = Math.max(
      0,
      (memoir.questionsSent || 0) - (memoir.storiesUploaded || 0)
    );

    return {
      _id: memoir._id,
      orderNumber: memoir.orderNumber,

      customer: {
        name: customerName,
        email: delivery.email || memoir.user?.email || '—',
        phone: delivery.phone || '—',
        joinDate: formatDate(memoir.user?.createdAt || memoir.createdAt),
        location:
          [delivery.city, delivery.state, delivery.country]
            .filter(Boolean)
            .join(', ') || '—'
      },

      memoir: {
        title: memoir.memoirTitle || 'Untitled Memoir',
        subtitle: memoir.memoirSubtitle || '',
        type: memoir.category?.name || '—',
        categoryId: memoir.category?._id || null,
        delivery: FREQUENCY_LABEL[memoir.questionFrequency] || 'Weekly',
        theme: THEME_LABEL[memoir.theme] || '—',
        pages: memoir.pages || 100,
        copies: memoir.copies || 1,
        status: STATUS_MAP[memoir.status]?.label || memoir.status,
        statusRaw: memoir.status,
        statusBadge:
          STATUS_MAP[memoir.status]?.badge || 'bg-gray-100 text-gray-700',
        statusDot: STATUS_MAP[memoir.status]?.dot || 'bg-gray-500',
        paymentStatus:
          PAYMENT_MAP[memoir.paymentStatus]?.label || memoir.paymentStatus,
        paymentBadge:
          PAYMENT_MAP[memoir.paymentStatus]?.badge ||
          'bg-gray-100 text-gray-700',
        totalAmount: memoir.totalAmount || 0,
        startDate: formatDate(memoir.startDate),
        estimatedDeliveryDate: formatDate(memoir.estimatedDeliveryDate),
        totalQuestions: totalQuestions || 0,
        questionsSent: memoir.questionsSent || 0,
        storiesUploaded: memoir.storiesUploaded || 0,
        pendingResponses,
        completionPercentage: completionPct,
        currentWeek: memoir.currentWeek || 0,
        totalWeeks: memoir.totalWeeks || 12,
        orderDate: formatShortDate(memoir.createdAt),
        isGuestOrder: !!memoir.isGuestOrder,
        adminNotes: memoir.adminNotes || '',
        customerNotes: memoir.customerNotes || ''
      },

      timeline: Array.isArray(memoir.timeline) ? memoir.timeline : [],
      uploadedStories: Array.isArray(memoir.uploadedStories)
        ? memoir.uploadedStories
        : []
    };
  }, [memoir]);

  /* ── Timeline rows: always computed, safe when memoir is null ──
     Key fix: `currentWeek + 1` is treated as the "pending" week — the
     one awaiting action — so the Send Questions button always shows. */
  const timelineRows = useMemo(() => {
    if (!memoirData || !memoir) return [];

    if (memoirData.timeline.length > 0) return memoirData.timeline;

    const totalWeeks = memoirData.memoir.totalWeeks || 12;
    const currentWeek = memoirData.memoir.currentWeek || 0;
    const startDate = memoir.startDate
      ? new Date(memoir.startDate)
      : memoir.createdAt
      ? new Date(memoir.createdAt)
      : new Date();

    const rows = [];
    for (let w = 1; w <= totalWeeks; w++) {
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + (w - 1) * 7);

      let status = 'upcoming';
      if (w <= currentWeek) {
        status = 'completed';
      } else if (w === currentWeek + 1) {
        // The very next week — awaiting the admin to send questions
        status = 'pending';
      }

      const perWeekSent =
        currentWeek > 0
          ? Math.round(memoirData.memoir.questionsSent / currentWeek)
          : 0;
      const perWeekUploaded =
        currentWeek > 0
          ? Math.round(memoirData.memoir.storiesUploaded / currentWeek)
          : 0;

      rows.push({
        id: w,
        week: w,
        dueDate: formatDate(dueDate),
        questionsSent: w <= currentWeek ? perWeekSent : 0,
        storiesUploaded: w <= currentWeek ? perWeekUploaded : 0,
        status
      });
    }
    return rows;
  }, [memoirData, memoir]);

  /* ── Filter uploaded stories by approval status ── */
  const { pendingStories, approvedStories } = useMemo(() => {
    const list = memoirData?.uploadedStories || [];
    return {
      pendingStories: list.filter(
        (s) => s.status === 'pending' || s.approvalStatus === 'pending'
      ),
      approvedStories: list.filter(
        (s) => s.status === 'approved' || s.approvalStatus === 'approved'
      )
    };
  }, [memoirData]);

  /* ── Early return AFTER all hooks ── */
  if (!isOpen || !memoir || !memoirData) return null;

  /* ═══════════════════════════════════════════════════════
     Handlers
     ═══════════════════════════════════════════════════════ */
  const handleSendQuestions = async (weekNumber) => {
    if (!onSendQuestions) return;
    try {
      setSendingWeek(weekNumber);
      await onSendQuestions(memoirData._id, weekNumber);
    } finally {
      setSendingWeek(null);
    }
  };

  /* ═══════════════════════════════════════════════════════
     Status badge renderer
     ═══════════════════════════════════════════════════════ */
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-[#E7F4ED] text-[#2F8F5B]">
            <span className="h-2 w-2 rounded-full bg-[#2F8F5B]" />
            Completed
            <ArrowDown size={12} className="text-[#2F8F5B]" />
          </span>
        );
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-[#FFF4D6] text-[#D8A100]">
            <span className="h-2 w-2 rounded-full bg-[#D8A100]" />
            Pending
            <ArrowDown size={12} className="text-[#D8A100]" />
          </span>
        );
      case 'upcoming':
        return (
          <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-[#F2F4F7] text-[#6B635D]">
            <span className="h-2 w-2 rounded-full bg-[#6B635D]" />
            Upcoming
            <ArrowDown size={12} className="text-[#6B635D]" />
          </span>
        );
      case 'pending-approval':
        return (
          <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-[#FFF4D6] text-[#D8A100]">
            <span className="h-2 w-2 rounded-full bg-[#D8A100]" />
            Pending Approval
            <ArrowDown size={12} className="text-[#D8A100]" />
          </span>
        );
      case 'approved':
        return (
          <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-[#E7F4ED] text-[#2F8F5B]">
            <span className="h-2 w-2 rounded-full bg-[#2F8F5B]" />
            Approved
            <ArrowDown size={12} className="text-[#2F8F5B]" />
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[80%] lg:w-[70%] xl:w-[50%] bg-white z-[999] overflow-y-auto transition-transform duration-300 ease-in-out shadow-xl">
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-y-auto">
            {/* ─────────── Header ─────────── */}
            <div className="border-b border-[#E4E4E4] p-4">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-[#1C2A46] mb-2">
                    {memoirData.memoir.title}
                  </h3>
                  {memoirData.memoir.subtitle && (
                    <p className="text-sm italic text-[#6B635D] mb-2">
                      {memoirData.memoir.subtitle}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-base text-[#0E1628]">
                      {memoirData.customer.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <EmailIcon size={18} color="#8A9099" />
                      <span className="text-sm text-[#8A9099]">
                        {memoirData.customer.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <PhoneIcon size={18} color="#8A9099" />
                      <span className="text-sm text-[#8A9099]">
                        {memoirData.customer.phone}
                      </span>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[#6B635D] text-sm font-light">
                      {memoirData.memoir.type}
                    </span>
                    <span className="bg-[#6B635D] w-1.5 h-1.5 rounded-full" />
                    <span className="text-[#6B635D] text-sm font-light">
                      {memoirData.memoir.delivery}
                    </span>
                    <span
                      className={`px-3 py-1.5 text-xs font-normal rounded-full ${memoirData.memoir.statusBadge}`}
                    >
                      {memoirData.memoir.status}
                    </span>
                    <span className="bg-[#6B635D] w-1.5 h-1.5 rounded-full" />
                    <span className="text-[#6B635D] text-sm font-light">
                      Order #{memoirData.orderNumber}
                    </span>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] transition-colors"
                >
                  <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
                </button>
              </div>
            </div>

            {/* ─────────── Stats Grid ─────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 px-4 pt-6">
              {/* Total Questions Sent */}
              <div className="bg-[#F9FAFB] p-5">
                <p className="text-sm text-[#6B635D] mb-4 flex items-center gap-2">
                  <SendICon
                    className="text-[#8F2F84] w-6 h-6"
                    size={10}
                    color="#8F2F84"
                  />
                  Total Questions Sent
                </p>
                <p className="text-3xl text-left font-medium text-[#0E1628]">
                  {memoirData.memoir.questionsSent}
                </p>
                <p className="text-xs text-left font-light text-[#6B635D] mt-3">
                  of {memoirData.memoir.totalQuestions} total questions
                </p>
              </div>

              {/* Stories Uploaded */}
              <div className="bg-[#F9FAFB] p-5">
                <p className="text-sm text-[#6B635D] mb-4 flex items-center gap-2">
                  <CheckCircleIcon
                    className="text-[#2F578F]"
                    size={20}
                    color="#2F578F"
                  />
                  Stories Uploaded
                </p>
                <p className="text-3xl text-left font-medium text-[#0E1628]">
                  {memoirData.memoir.storiesUploaded}
                </p>
                <p className="text-xs text-left font-light text-[#6B635D] mt-3">
                  {memoirData.memoir.completionPercentage}% complete
                </p>
                <div className="mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#2F578F] rounded-full"
                    style={{
                      width: `${memoirData.memoir.completionPercentage}%`
                    }}
                  />
                </div>
              </div>

              {/* Pending Responses */}
              <div className="bg-[#F9FAFB] p-5">
                <p className="text-sm text-[#6B635D] mb-4 flex items-center gap-2">
                  <CautionIcon
                    className="text-[#8F7F2F]"
                    size={20}
                    color="#8F7F2F"
                  />
                  Pending Responses
                </p>
                <p className="text-3xl text-left font-medium text-[#0E1628]">
                  {memoirData.memoir.pendingResponses}
                </p>
                <p className="text-xs text-left font-light text-[#6B635D] mt-3">
                  awaiting upload from WhatsApp
                </p>
              </div>
            </div>

            {/* ─────────── Meta row ─────────── */}
            <div className="px-4 pb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white border border-[#E4E4E4] p-3 text-left">
                  <p className="text-xs text-[#8A9099] mb-1">Theme</p>
                  <p className="text-[#0E1628] font-medium">
                    {memoirData.memoir.theme}
                  </p>
                </div>
                <div className="bg-white border border-[#E4E4E4] p-3 text-left">
                  <p className="text-xs text-[#8A9099] mb-1">Pages</p>
                  <p className="text-[#0E1628] font-medium">
                    {memoirData.memoir.pages}
                  </p>
                </div>
                <div className="bg-white border border-[#E4E4E4] p-3 text-left">
                  <p className="text-xs text-[#8A9099] mb-1">Copies</p>
                  <p className="text-[#0E1628] font-medium">
                    {memoirData.memoir.copies}
                  </p>
                </div>
                <div className="bg-white border border-[#E4E4E4] p-3 text-left">
                  <p className="text-xs text-[#8A9099] mb-1">Payment</p>
                  <p
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${memoirData.memoir.paymentBadge}`}
                  >
                    {memoirData.memoir.paymentStatus}
                  </p>
                </div>
              </div>
            </div>

            {/* ─────────── Tabs ─────────── */}
            <div className="border-b border-[#DBDBDB] mx-4 mb-5">
              <div className="flex items-center">
                <button
                  onClick={() => setActiveStoryTab('timelines')}
                  className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                    activeStoryTab === 'timelines'
                      ? 'text-[#55000C] border-b-2 border-[#55000C]'
                      : 'text-[#6B635D] hover:text-[#1C2A46]'
                  }`}
                >
                  Timelines & Questions
                </button>
                <button
                  onClick={() => setActiveStoryTab('uploaded')}
                  className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                    activeStoryTab === 'uploaded'
                      ? 'text-[#55000C] border-b-2 border-[#55000C]'
                      : 'text-[#6B635D] hover:text-[#1C2A46]'
                  }`}
                >
                  Uploaded Stories ({memoirData.memoir.storiesUploaded})
                </button>
              </div>
            </div>

            {/* ─────────── Timelines Tab ─────────── */}
            {activeStoryTab === 'timelines' && (
              <div className="border border-[#E4E4E4] bg-white mx-4 mb-6">
                <div className="px-4 py-3 flex items-center justify-between border-b border-[#E4E4E4] bg-white">
                  <h4 className="text-base font-medium text-[#0E1628]">
                    Question Delivery Timeline
                  </h4>
                  <p className="text-sm text-[#8A9099]">
                    Order Date: {memoirData.memoir.orderDate}
                  </p>
                </div>

                {timelineRows.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#6B635D]">
                    No timeline data yet.
                  </div>
                ) : (
                  <div className="divide-y divide-[#E4E4E4]">
                    {timelineRows.map((week) => (
                      <div
                        key={week.id || week.week}
                        className="p-5 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3 text-left">
                          <div>
                            <p className="text-sm font-medium text-[#0E1628]">
                              Week {week.week}
                            </p>
                            <p className="text-xs text-[#6B635D] mt-0.5">
                              Due: {week.dueDate}
                            </p>
                          </div>
                          <div>{getStatusBadge(week.status)}</div>
                        </div>

                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <SendICon
                                className="text-[#8F2F84] w-5 h-5"
                                color="#8F2F84"
                              />
                              <span className="text-sm text-[#0E1628]">
                                {week.questionsSent} Questions sent
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <ChatIcon
                                className="text-[#194F9B] w-5 h-5"
                                size={18}
                                color="#194F9B"
                              />
                              <span className="text-sm text-[#0E1628]">
                                {week.storiesUploaded} Stories Uploaded
                              </span>
                            </div>
                          </div>

                          {week.status === 'pending' && (
                            <button
                              onClick={() => handleSendQuestions(week.week)}
                              disabled={sendingWeek === week.week}
                              className={`px-4 py-2 text-white text-sm font-medium transition-colors ${
                                sendingWeek === week.week
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-[#D2A146] hover:bg-[#C49660]'
                              }`}
                            >
                              {sendingWeek === week.week
                                ? 'Sending…'
                                : 'Send Questions'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─────────── Uploaded Stories Tab ─────────── */}
            {activeStoryTab === 'uploaded' && (
              <div className="mx-4 mb-6">
                {pendingStories.length === 0 && approvedStories.length === 0 && (
                  <div className="p-8 text-center text-sm text-[#6B635D] border border-[#E4E4E4] bg-white">
                    No stories uploaded yet. Stories will appear here as the
                    customer submits them.
                  </div>
                )}

                {/* Pending Approval */}
                {pendingStories.length > 0 && (
                  <>
                    <div className="px-4 py-3 flex items-center justify-between border border-[#E4E4E4]">
                      <h4 className="text-base font-normal text-[#1C2A46]">
                        Pending Approval{' '}
                        <span className="bg-[#FFF4D6] text-[#D8A100] px-1.5 py-0.5 rounded-full">
                          {pendingStories.length}
                        </span>
                      </h4>
                      <button className="text-sm font-medium text-[#0E1628] hover:text-[#C49660]">
                        Approve All Response
                      </button>
                    </div>

                    <div className="space-y-4">
                      {pendingStories.map((story) => (
                        <div
                          key={story.id || story._id}
                          className="mt-5 mb-5 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between text-left">
                            <div>
                              <p className="text-sm font-normal text-[#8A9099]">
                                Week {story.week} • Received{' '}
                                {story.receivedDate || '—'}
                              </p>
                              <h5 className="text-base font-medium text-[#0E1628] mb-2 text-left">
                                {story.question}
                              </h5>
                            </div>
                            <button className="px-4 py-2 bg-[#D2A146] text-white text-sm font-medium hover:bg-[#C49660] transition-colors">
                              Pending Approval
                            </button>
                          </div>

                          <div className="flex-col mt-5 items-center justify-between border border-[#E4E4E4] p-2.5">
                            <p className="text-sm text-[#6B635D] mb-3 text-left line-clamp-2">
                              {story.response}
                            </p>
                            <button className="text-sm text-[#55000C] hover:underline font-normal flex items-center gap-1 group">
                              Read Full Response
                              <span className="group-hover:translate-x-1 transition-transform">
                                →
                              </span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Approved Stories */}
                {approvedStories.length > 0 && (
                  <>
                    <div className="px-4 py-3 border border-[#E4E4E4] flex mt-5">
                      <h4 className="text-lg font-medium text-[#0E1628]">
                        Approved Stories{' '}
                        <span className="bg-[#E7F4ED] text-[#2F8F5B] px-1.5 py-0.5 rounded-full">
                          {approvedStories.length}
                        </span>
                      </h4>
                    </div>

                    <div>
                      {approvedStories.map((story) => (
                        <div
                          key={story.id || story._id}
                          className="p-5 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3 text-left">
                            <div>
                              <p className="text-sm font-normal text-[#8A9099]">
                                Week {story.week} • {story.receivedDate || '—'}
                              </p>
                              <h5 className="text-base font-medium text-[#0E1628] mb-2 text-left">
                                {story.question}
                              </h5>
                            </div>
                            <div className="flex gap-5 items-center">
                              <EditPencil className="w-5 h-5" />
                              <DeleteICon color="#C03A2B" size={24} />
                            </div>
                          </div>

                          <div className="flex-col mt-5 items-center justify-between border border-[#E4E4E4] p-2.5">
                            <p className="text-sm text-[#6B635D] mb-3 text-left line-clamp-2">
                              {story.response}
                            </p>
                            <button className="text-sm text-[#55000C] hover:underline font-normal flex items-center gap-1 group">
                              Read Full Response
                              <span className="group-hover:translate-x-1 transition-transform">
                                →
                              </span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerMemoirDetailModal;